/**
 * FEAT-010 — Vehicle profile library store (workspace-wide).
 *
 * Unlike most stores in this app, the profile list is shared across every
 * station. Mutations go straight to Dexie (via VehicleRepositoryDexie) and the
 * store mirrors the result. Tests substitute their own repo by reaching into
 * `__setRepoForTests`.
 *
 * Curve modelling was deferred (Michal, 2026-05-25). The v1 model uses three
 * power caps + battery size + a default starting SoC.
 */
import { create } from 'zustand';
import { db } from '@/persistence/dexie/db';
import { VehicleRepositoryDexie } from '@/persistence/dexie/repositories';
import type { VehicleRepository } from '@/persistence/ports';
import type { ChargingCurvePoint, VehicleProfileSnapshot } from '@/domain';
import { vehicleProfileSchema } from '@/domain/VehicleProfile';
import { VehicleProfileId, type VehicleProfileId as VehicleProfileIdT } from '@/domain/types';
import { newVehicleProfileId } from '@/domain/newVehicleProfileId';

export interface VehicleStoreState {
  readonly profiles: readonly VehicleProfileSnapshot[];
  readonly status: 'idle' | 'loading' | 'ready';
}

export interface NewVehicleInput {
  readonly name: string;
  readonly batteryKwh: number;
  readonly maxAcPowerKw: number;
  readonly maxDcPowerKw: number;
  readonly defaultStartSocPct: number;
  /**
   * Optional DC charging curve. Empty array clears any existing curve (treated
   * as "flat maxDcPowerKw"); omitted (undefined) leaves the existing curve
   * untouched on update.
   */
  readonly chargingCurve?: readonly ChargingCurvePoint[];
}

export interface MutationResult {
  readonly ok: boolean;
  readonly id?: VehicleProfileIdT;
  readonly error?: string;
}

export interface ImportResult {
  readonly ok: boolean;
  readonly imported: number;
  readonly skipped: number;
  readonly error?: string;
}

export interface VehicleStoreActions {
  loadAll(): Promise<void>;
  create(input: NewVehicleInput): Promise<MutationResult>;
  update(id: VehicleProfileIdT, patch: Partial<NewVehicleInput>): Promise<MutationResult>;
  remove(id: VehicleProfileIdT): Promise<void>;
  duplicate(id: VehicleProfileIdT): Promise<MutationResult>;
  importJson(text: string): Promise<ImportResult>;
  exportJson(): string;
}

export type VehicleStore = VehicleStoreState & VehicleStoreActions;

let repo: VehicleRepository = new VehicleRepositoryDexie(db);

/** Test-only — swap the repository binding for fakes. */
export function __setRepoForTests(next: VehicleRepository): void {
  repo = next;
}

function validateName(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return 'Name is required.';
  if (trimmed.length > 64) return 'Name must be 64 characters or fewer.';
  return null;
}

function validatePower(input: NewVehicleInput): string | null {
  if (!Number.isFinite(input.batteryKwh) || input.batteryKwh <= 0) {
    return 'batteryKwh must be > 0.';
  }
  if (!Number.isFinite(input.maxAcPowerKw) || input.maxAcPowerKw < 0) {
    return 'maxAcPowerKw must be ≥ 0.';
  }
  if (!Number.isFinite(input.maxDcPowerKw) || input.maxDcPowerKw < 0) {
    return 'maxDcPowerKw must be ≥ 0.';
  }
  if (input.maxAcPowerKw === 0 && input.maxDcPowerKw === 0) {
    return 'Vehicle must accept either AC or DC power (both can\'t be 0).';
  }
  if (
    !Number.isFinite(input.defaultStartSocPct) ||
    input.defaultStartSocPct < 0 ||
    input.defaultStartSocPct > 100
  ) {
    return 'defaultStartSocPct must be in [0, 100].';
  }
  return null;
}

function validateCurve(curve: readonly ChargingCurvePoint[] | undefined): string | null {
  if (curve === undefined) return null;
  for (const p of curve) {
    if (!Number.isFinite(p.socPct) || p.socPct < 0 || p.socPct > 100) {
      return 'Charging-curve SoC must be in [0, 100].';
    }
    if (!Number.isFinite(p.powerKw) || p.powerKw < 0) {
      return 'Charging-curve power must be ≥ 0 kW.';
    }
  }
  return null;
}

/** Drop an empty curve (→ flat cap) and sort the rest ascending by SoC. */
function normalizeCurve(
  curve: readonly ChargingCurvePoint[] | undefined,
): readonly ChargingCurvePoint[] | undefined {
  if (curve === undefined || curve.length === 0) return undefined;
  return [...curve].sort((a, b) => a.socPct - b.socPct);
}

function nextDuplicateName(base: string, existing: readonly VehicleProfileSnapshot[]): string {
  const taken = new Set(existing.map((p) => p.name));
  if (!taken.has(`${base} (copy)`)) return `${base} (copy)`;
  for (let i = 2; i < 1000; i++) {
    const candidate = `${base} (copy ${i})`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base} (copy ${Date.now()})`;
}

export const useVehicleStore = create<VehicleStore>((set, get) => ({
  profiles: [],
  status: 'idle',

  async loadAll(): Promise<void> {
    set({ status: 'loading' });
    const profiles = await repo.list();
    set({ profiles, status: 'ready' });
  },

  async create(input): Promise<MutationResult> {
    const nameErr = validateName(input.name);
    if (nameErr) return { ok: false, error: nameErr };
    const powerErr = validatePower(input);
    if (powerErr) return { ok: false, error: powerErr };
    const curveErr = validateCurve(input.chargingCurve);
    if (curveErr) return { ok: false, error: curveErr };

    const id = newVehicleProfileId();
    const curve = normalizeCurve(input.chargingCurve);
    const snap: VehicleProfileSnapshot = {
      id,
      name: input.name.trim(),
      batteryKwh: input.batteryKwh,
      maxAcPowerKw: input.maxAcPowerKw,
      maxDcPowerKw: input.maxDcPowerKw,
      defaultStartSocPct: input.defaultStartSocPct,
      ...(curve ? { chargingCurve: curve } : {}),
      isPreset: false,
      createdAt: Date.now(),
    };
    await repo.upsert(snap);
    const profiles = await repo.list();
    set({ profiles });
    return { ok: true, id };
  },

  async update(id, patch): Promise<MutationResult> {
    const existing = await repo.get(id);
    if (!existing) return { ok: false, error: 'Vehicle not found.' };

    // `?? existing` keeps the current curve when the patch omits it; an
    // explicit `[]` in the patch clears it (normalizeCurve → undefined).
    const mergedCurve = patch.chargingCurve ?? existing.chargingCurve;
    const merged: NewVehicleInput = {
      name: patch.name ?? existing.name,
      batteryKwh: patch.batteryKwh ?? existing.batteryKwh,
      maxAcPowerKw: patch.maxAcPowerKw ?? existing.maxAcPowerKw,
      maxDcPowerKw: patch.maxDcPowerKw ?? existing.maxDcPowerKw,
      defaultStartSocPct: patch.defaultStartSocPct ?? existing.defaultStartSocPct,
      ...(mergedCurve !== undefined ? { chargingCurve: mergedCurve } : {}),
    };
    const nameErr = validateName(merged.name);
    if (nameErr) return { ok: false, error: nameErr };
    const powerErr = validatePower(merged);
    if (powerErr) return { ok: false, error: powerErr };
    const curveErr = validateCurve(merged.chargingCurve);
    if (curveErr) return { ok: false, error: curveErr };

    const curve = normalizeCurve(merged.chargingCurve);
    // Build explicitly (no `...existing` spread) so clearing the curve sticks.
    const next: VehicleProfileSnapshot = {
      id: existing.id,
      name: merged.name.trim(),
      batteryKwh: merged.batteryKwh,
      maxAcPowerKw: merged.maxAcPowerKw,
      maxDcPowerKw: merged.maxDcPowerKw,
      defaultStartSocPct: merged.defaultStartSocPct,
      ...(curve ? { chargingCurve: curve } : {}),
      isPreset: existing.isPreset,
      createdAt: existing.createdAt,
    };
    await repo.upsert(next);
    const profiles = await repo.list();
    set({ profiles });
    return { ok: true, id };
  },

  async remove(id): Promise<void> {
    await repo.delete(id);
    const profiles = await repo.list();
    set({ profiles });
  },

  async duplicate(id): Promise<MutationResult> {
    const existing = await repo.get(id);
    if (!existing) return { ok: false, error: 'Vehicle not found.' };
    const newId = newVehicleProfileId();
    const snap: VehicleProfileSnapshot = {
      ...existing,
      id: newId,
      name: nextDuplicateName(existing.name, get().profiles),
      // Copies of presets are user-owned.
      isPreset: false,
      createdAt: Date.now(),
    };
    await repo.upsert(snap);
    const profiles = await repo.list();
    set({ profiles });
    return { ok: true, id: newId };
  },

  async importJson(text): Promise<ImportResult> {
    let raw: unknown;
    try {
      raw = JSON.parse(text);
    } catch {
      return { ok: false, imported: 0, skipped: 0, error: 'Invalid JSON.' };
    }
    const items: unknown[] = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as { profiles?: unknown }).profiles)
        ? ((raw as { profiles: unknown[] }).profiles)
        : [raw];

    const existing = await repo.list();
    const byName = new Map(existing.map((p) => [p.name, p] as const));
    let imported = 0;
    let skipped = 0;

    for (const item of items) {
      const parsed = vehicleProfileSchema.safeParse(item);
      if (!parsed.success) {
        skipped++;
        continue;
      }
      const data = parsed.data;
      // De-dupe by name: skip rows whose name collides with an existing profile.
      if (byName.has(data.name)) {
        skipped++;
        continue;
      }
      const id =
        data.id && data.id.length > 0
          ? safeId(data.id) ?? newVehicleProfileId()
          : newVehicleProfileId();
      const curve = normalizeCurve(data.chargingCurve);
      const snap: VehicleProfileSnapshot = {
        id,
        name: data.name,
        batteryKwh: data.batteryKwh,
        maxAcPowerKw: data.maxAcPowerKw,
        maxDcPowerKw: data.maxDcPowerKw,
        defaultStartSocPct: data.defaultStartSocPct,
        ...(curve ? { chargingCurve: curve } : {}),
        isPreset: data.isPreset ?? false,
        createdAt: data.createdAt ?? Date.now(),
      };
      await repo.upsert(snap);
      byName.set(snap.name, snap);
      imported++;
    }

    const profiles = await repo.list();
    set({ profiles });
    return { ok: true, imported, skipped };
  },

  exportJson(): string {
    const profiles = get().profiles;
    return JSON.stringify({ profiles }, null, 2);
  },
}));

function safeId(raw: string): VehicleProfileIdT | null {
  try {
    return VehicleProfileId.of(raw);
  } catch {
    return null;
  }
}
